package dev.lingvo.site.components.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateMapOf
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf

internal enum class EditorAssistStatusSourceKind {
    TRANSLATION_MEMORY,
    PROVIDER,
    PROPOSAL,
}

internal enum class EditorAssistStatusEventKind {
    LOADING,
    RUNNING,
    PENDING,
    SUCCEEDED,
    NO_RESULT,
    FAILED,
    CANCELLED,
    CLEARED,
}

internal data class EditorAssistStatusScope(
    val projectSlug: String,
    val locale: String,
    val keyId: String,
)

internal data class EditorAssistStatusPublication(
    val scope: EditorAssistStatusScope,
    val sourceKind: EditorAssistStatusSourceKind,
    val sourceId: String,
    val eventKind: EditorAssistStatusEventKind,
    val publicationId: Long,
    val message: String,
    val toastFeedback: EditorAssistToastFeedback? = null,
)

internal data class EditorAssistToastFeedback(
    val message: String,
    val variant: LToastVariant,
)

internal data class EditorAssistStatusSession(
    val scope: EditorAssistStatusScope,
    val presenceGeneration: Long,
    val baselinePublicationId: Long,
    val sessionId: Long,
)

private data class EditorAssistStatusSessionRecord(
    val session: EditorAssistStatusSession,
    val unannouncedActivePublicationIds: MutableSet<Long>,
    val claimedPublicationIds: MutableSet<Long>,
    val committedPublicationIds: MutableSet<Long>,
)

/**
 * One ordered status journal for every Assist producer.
 *
 * The queue is capped per project/locale/key scope. Each retained publication still carries its
 * exact source and event kind, while one bounded list preserves the only order consumers need.
 */
@Stable
internal class EditorAssistStatusStore {
    private val publicationsByScope =
        mutableStateMapOf<EditorAssistStatusScope, List<EditorAssistStatusPublication>>()
    private val publicationScopeOrder = mutableListOf<EditorAssistStatusScope>()
    private val sessions = linkedMapOf<Long, EditorAssistStatusSessionRecord>()
    private var nextPublicationId = 1L
    private var nextSessionId = 1L

    fun publish(
        projectSlug: String,
        locale: String,
        keyId: String,
        sourceKind: EditorAssistStatusSourceKind,
        sourceId: String,
        eventKind: EditorAssistStatusEventKind,
        message: String,
        toastFeedback: EditorAssistToastFeedback? = null,
    ): EditorAssistStatusPublication {
        val scope = EditorAssistStatusScope(
            projectSlug = projectSlug,
            locale = locale,
            keyId = keyId,
        )
        val publication = EditorAssistStatusPublication(
            scope = scope,
            sourceKind = sourceKind,
            sourceId = sourceId,
            eventKind = eventKind,
            publicationId = nextPublicationId++,
            message = message,
            toastFeedback = toastFeedback,
        )
        publicationScopeOrder.remove(scope)
        publicationScopeOrder.add(scope)
        publicationsByScope[scope] =
            (publicationsByScope[scope].orEmpty() + publication).takeLast(MAX_EVENTS_PER_SCOPE)
        trimPublicationScopes()
        return publication
    }

    fun publishTranslationMemory(
        projectSlug: String,
        locale: String,
        keyId: String,
        eventKind: EditorAssistStatusEventKind,
        message: String,
        toastFeedback: EditorAssistToastFeedback? = null,
    ): EditorAssistStatusPublication = publish(
        projectSlug = projectSlug,
        locale = locale,
        keyId = keyId,
        sourceKind = EditorAssistStatusSourceKind.TRANSLATION_MEMORY,
        sourceId = TRANSLATION_MEMORY_STATUS_SOURCE_ID,
        eventKind = eventKind,
        message = message,
        toastFeedback = toastFeedback,
    )

    fun publishProvider(
        claim: EditorGenerationClaim,
        eventKind: EditorAssistStatusEventKind,
        message: String,
        toastFeedback: EditorAssistToastFeedback? = null,
    ): EditorAssistStatusPublication = publish(
        projectSlug = claim.projectSlug,
        locale = claim.locale,
        keyId = claim.keyId,
        sourceKind = EditorAssistStatusSourceKind.PROVIDER,
        sourceId = claim.provider.id,
        eventKind = eventKind,
        message = message,
        toastFeedback = toastFeedback,
    )

    fun publishProposal(
        claim: EditorProposalActionClaim,
        eventKind: EditorAssistStatusEventKind,
        message: String,
        toastFeedback: EditorAssistToastFeedback? = null,
    ): EditorAssistStatusPublication = publish(
        projectSlug = claim.projectSlug,
        locale = claim.locale,
        keyId = claim.keyId,
        sourceKind = EditorAssistStatusSourceKind.PROPOSAL,
        sourceId = claim.proposalId,
        eventKind = eventKind,
        message = message,
        toastFeedback = toastFeedback,
    )

    fun publicationsFor(
        projectSlug: String,
        locale: String,
        keyId: String,
    ): List<EditorAssistStatusPublication> = publicationsByScope[
        EditorAssistStatusScope(projectSlug, locale, keyId)
    ].orEmpty()

    internal fun retainedPublicationScopeCount(): Int = publicationsByScope.size

    /**
     * Freezes one journal baseline for one mounted live node.
     *
     * Only an active live-region presence registers this boundary. Settled history stays behind the
     * latest-publication cursor. Publications for work still active at the boundary are retained
     * separately for one announcement, so one long-running request cannot pull unrelated
     * closed-session history back into the live region.
     */
    fun registerSession(
        projectSlug: String,
        locale: String,
        keyId: String,
        presenceGeneration: Long,
        activePublicationIds: Collection<Long>,
    ): EditorAssistStatusSession {
        val scope = EditorAssistStatusScope(projectSlug, locale, keyId)
        val latestPublicationId = publicationsByScope[scope]
            ?.lastOrNull()
            ?.publicationId
            ?: 0L
        val session = EditorAssistStatusSession(
            scope = scope,
            presenceGeneration = presenceGeneration,
            baselinePublicationId = latestPublicationId,
            sessionId = nextSessionId++,
        )
        val retainedPublicationIds = publicationsByScope[scope]
            .orEmpty()
            .mapTo(mutableSetOf(), EditorAssistStatusPublication::publicationId)
        sessions[session.sessionId] = EditorAssistStatusSessionRecord(
            session = session,
            unannouncedActivePublicationIds = activePublicationIds
                .asSequence()
                .filter { publicationId -> publicationId in retainedPublicationIds }
                .toMutableSet(),
            claimedPublicationIds = mutableSetOf(),
            committedPublicationIds = mutableSetOf(),
        )
        while (sessions.size > MAX_SESSIONS) {
            sessions.remove(sessions.keys.first())
        }
        return session
    }

    /**
     * Claims every queued publication for the live node without yet treating it as announced.
     * [commitAnnouncement] closes that gap only after the message is present in the committed DOM.
     */
    fun consumeUnannounced(
        session: EditorAssistStatusSession,
    ): List<EditorAssistStatusPublication> {
        val record = sessions[session.sessionId]
            ?.takeIf { existing -> existing.session == session }
            ?: return emptyList()
        val retained = publicationsByScope[session.scope].orEmpty()
        record.pruneTo(retained)
        val claimed = retained.filter { publication ->
            publication.isEligibleFor(record) &&
                publication.publicationId !in record.claimedPublicationIds &&
                publication.publicationId !in record.committedPublicationIds
        }
        claimed.mapTo(record.claimedPublicationIds, EditorAssistStatusPublication::publicationId)
        return claimed
    }

    /**
     * Marks the exact publications whose combined message has reached the live node's committed
     * DOM. A close before this effect runs leaves them claimed, so [drainUncommitted] can preserve
     * the outcome through a toast instead of silently losing it.
     */
    fun commitAnnouncement(
        session: EditorAssistStatusSession,
        publicationIds: Collection<Long>,
    ) {
        val record = recordFor(session) ?: return
        publicationIds.forEach { publicationId ->
            if (publicationId in record.claimedPublicationIds) {
                record.claimedPublicationIds.remove(publicationId)
                record.committedPublicationIds.add(publicationId)
                record.unannouncedActivePublicationIds.remove(publicationId)
            }
        }
    }

    /**
     * Transfers every publication not yet committed to the live DOM to the caller, atomically.
     * The caller may ignore non-terminal events, but none can replay after this session retires.
     */
    fun drainUncommitted(
        session: EditorAssistStatusSession,
    ): List<EditorAssistStatusPublication> {
        val record = recordFor(session) ?: return emptyList()
        val retained = publicationsByScope[session.scope].orEmpty()
        record.pruneTo(retained)
        val uncommitted = retained.filter { publication ->
            publication.isEligibleFor(record) &&
                publication.publicationId !in record.committedPublicationIds
        }
        uncommitted.forEach { publication ->
            record.claimedPublicationIds.remove(publication.publicationId)
            record.committedPublicationIds.add(publication.publicationId)
            record.unannouncedActivePublicationIds.remove(publication.publicationId)
        }
        return uncommitted
    }

    fun retireSession(session: EditorAssistStatusSession) {
        sessions[session.sessionId]?.takeIf { record -> record.session == session }?.let {
            sessions.remove(session.sessionId)
        }
    }

    private fun recordFor(session: EditorAssistStatusSession): EditorAssistStatusSessionRecord? {
        return sessions[session.sessionId]?.takeIf { record -> record.session == session }
    }

    private fun EditorAssistStatusPublication.isEligibleFor(
        record: EditorAssistStatusSessionRecord,
    ): Boolean = publicationId > record.session.baselinePublicationId ||
        publicationId in record.unannouncedActivePublicationIds

    private fun EditorAssistStatusSessionRecord.pruneTo(
        retained: List<EditorAssistStatusPublication>,
    ) {
        val retainedIds = retained.mapTo(mutableSetOf(), EditorAssistStatusPublication::publicationId)
        unannouncedActivePublicationIds.retainAll(retainedIds)
        claimedPublicationIds.retainAll(retainedIds)
        committedPublicationIds.retainAll(retainedIds)
    }

    private fun trimPublicationScopes() {
        while (publicationsByScope.size > MAX_PUBLICATION_SCOPES) {
            val protectedScopes = sessions.values
                .asSequence()
                .map { record -> record.session.scope }
                .toSet()
            val evicted = publicationScopeOrder.firstOrNull { scope -> scope !in protectedScopes }
                ?: return
            publicationScopeOrder.remove(evicted)
            publicationsByScope.remove(evicted)
        }
    }

    private companion object {
        const val MAX_EVENTS_PER_SCOPE = 32
        const val MAX_PUBLICATION_SCOPES = 96
        const val MAX_SESSIONS = 64
    }
}

internal const val TRANSLATION_MEMORY_STATUS_SOURCE_ID = "translation-memory"

/**
 * The cancellation notice, named here so [translationMemoryFailureMessage] can recognise its own.
 *
 * It is the one failure this module writes that already says what failed, and the only way to know
 * that in a language whose word for it is not the English substring below is to compare with the
 * string we produced.
 */
internal fun translationMemoryCancelledMessage(): String = tr(
    "editor.editor_assist_status_store.translation_memory_loading_was_cancelled",
    "Translation memory loading was cancelled. Retry?",
)

/**
 * Prefixes a failure with the subject, unless it already carries one.
 *
 * The substring test reads English because that is the language an API or a browser error arrives
 * in; the equality test covers this module's own notice, which is translated and would otherwise
 * be introduced by its own subject a second time.
 */
internal fun translationMemoryFailureMessage(message: String): String =
    if (
        message == translationMemoryCancelledMessage() ||
        message.replace('-', ' ').contains("translation memory", ignoreCase = true)
    ) {
        message
    } else {
        trf(
            "editor.editor_assist_status_store.translation_memory_unavailable",
            "Translation memory unavailable. {detail}",
            "detail" to message,
        )
    }
